import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Collapse from '@mui/material/Collapse';
import { withStyles } from '@mui/styles';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { Translate } from 'react-localize-redux';
import { Button, DropdownField, SwitchField, TextField } from '../../components/widgets';
import StatusLabel from '../../components/widgets/StatusLabel';
import css from './EditSegment.css';

const styles = {
    container: {
        overflow: 'unset',
    },
};

const EditSegment = props => {
    const {
        segment,
        editingThisSegment,
        editSegmentStart,
        editSegmentChange,
        editSegmentSave,
        editSegmentCancel,
        classes,
        isAbTestConnected,
        readOnly,
    } = props;

    const initialActive = (() => {
        if (segment && segment.edit && segment.edit.config && 'active' in segment.edit.config) {
            return segment.edit.config.active;
        }
        return false;
    })();

    const [active, setActive] = useState(initialActive);
    const prevDoneRef = useRef(segment && segment.edit && segment.edit.config ? segment.edit.config.done : undefined);

    useEffect(() => {
        const done = segment && segment.edit && segment.edit.config ? segment.edit.config.done : undefined;
        if (done && !prevDoneRef.current) {
            const nextActive = segment && segment.edit && segment.edit.config ? segment.edit.config.active : false;
            setActive(nextActive);
        }
        prevDoneRef.current = done;
    }, [
        segment && segment.edit && segment.edit.config ? segment.edit.config.done : undefined,
        segment && segment.edit && segment.edit.config ? segment.edit.config.active : undefined,
    ]);

    const onEditSegmentChange = (key, value) => {
        editSegmentChange(key, value, segment);
    };

    const { edit, open } = segment;
    let fields = [];
    if (edit && edit.fields) {
        fields = edit.fields;
    }

    return (
        <Card className={classNames(css.cardContainer, { [css.open]: open, [css.readOnly]: readOnly })}>
            <CardContent className={css.cardContentContainer}>
                <div className={css.cardContent} onClick={() => editSegmentStart(segment)}>
                    <div className={css.arrow} />
                    <div role="button" className={css.cardContentHeader} tabIndex={0}>
                        {segment.name}
                    </div>
                    {isAbTestConnected ? (
                        <div className={css.testTubeContainer}>
                            <Translate id="STATIC.PAGES.AUDIENCES.INCREMENTALITY_TEST" />
                        </div>
                    ) : (
                        <StatusLabel
                            status={active ? 'active' : 'inactive'}
                            label={`STATIC.PAGES.AUDIENCES.${active ? 'ON' : 'OFF'}`}
                        />
                    )}
                </div>
                <Collapse
                    in={open}
                    timeout="auto"
                    unmountOnExit
                    classes={{
                        container: classes.container,
                    }}
                >
                    <SwitchField
                        checked={edit.config.active}
                        disabled={isAbTestConnected || readOnly}
                        onChange={checked => onEditSegmentChange('active', checked)}
                        containerClass={css.switchContainerSegment}
                        onClick={event => event.stopPropagation()}
                        label="STATIC.PAGES.AUDIENCES.ENABLE_SEGMENT_DISTRIBUTION_TOGGLE_LABEL"
                        type="small"
                    />
                    {fields &&
                        fields.map(field => {
                            switch (field.ui_type) {
                                case 'dropdown':
                                    return (
                                        <DropdownField
                                            disabled={isAbTestConnected || readOnly}
                                            key={field.name}
                                            label={field.display}
                                            items={field.choices.map(c => ({
                                                name: c.name,
                                                display_name: c.display,
                                            }))}
                                            selected={edit.config[field.name]}
                                            onChange={value => onEditSegmentChange(field.name, value)}
                                        />
                                    );
                                case 'text':
                                case 'secret':
                                default:
                                    return (
                                        <TextField
                                            disabled={isAbTestConnected || readOnly}
                                            key={field.name}
                                            label={field.display}
                                            value={edit.config[field.name]}
                                            password={field.ui_type === 'secret'}
                                            onChange={value => onEditSegmentChange(field.name, value)}
                                            autoCompleteOff
                                            id={`textfield_seg_${segment.id}_field_${field.name}`}
                                        />
                                    );
                            }
                        })}
                </Collapse>
            </CardContent>
            {open && !readOnly && (
                <CardActions className={css.cardActions}>
                    <Button type="flat" level="level1" onClick={() => editSegmentCancel(segment)}>
                        <Translate id="STATIC.BUTTONS.CANCEL" />
                    </Button>
                    <Button
                        type="primary"
                        level="important"
                        showV={edit.config.done}
                        forceActive={edit.config.done || edit.config.saving}
                        onClick={() => editSegmentSave(segment)}
                    >
                        <Translate id="STATIC.BUTTONS.SAVE" />
                    </Button>
                </CardActions>
            )}
        </Card>
    );
};

export default withStyles(styles)(EditSegment);
